<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
  >
    <template v-slot:header>
      <div class="d-flex justify-space-between w-100">
        <v-card-title class="screen-title"
          >イントラネットFW ゾーン選択</v-card-title
        >
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="closeModal"
        >
          ×
        </button>
      </div>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="d-flex flex-wrap ga-3 justify-end my-4">
            <v-btn color="primary" @click="downloadConfig" v-permission.config
              >設定ダウンロード
            </v-btn>
            <v-btn color="primary" @click="uploadConfig" v-permission.config
              >設定アップロード
            </v-btn>
          </div>
          <app-table
            primary-key="zoneRelationId"
            v-model:table-sort-by="zoneSelect.sortBy"
            :fields="zoneSelect.fields"
            :items="zoneSelect.items"
            :per-page="-1"
            :hover="true"
            @click:row="ShowIntranetFwPolicyList"
          >
            <template #cell(arrow)>⇒</template>
          </app-table>
          <v-alert class="my-3" v-if="!!uploadAlert">{{ uploadAlert }}</v-alert>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn
              variant="flat"
              color="primary"
              @click="putIntranetFwPolicy"
              v-permission.config
            >
              ポリシー設定反映
            </v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { ZoneRelation, ZoneRelationItem } from "@/apis/ZoneRelationApi";
import type {
  IntranetFwPolicy,
  PostIntranetPolicy,
} from "@/apis/IntranetfwPolicyApi";
import type { IntranetfwServiceMenu } from "@/apis/IntranetfwServiceMenuApi";
import { UploadError } from "@/services/ConfigDownloadService";
import type { SortItem } from "@/components/AppTable.vue";
import { VueFinalModal } from "vue-final-modal";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import IntranetFwPolicyList from "@/modals/applianceContractSetting/IntranetFirewall/policy/ifw/IntranetFwPolicyList.vue";
import IntranetFwPolicyConfirm from "@/modals/applianceContractSetting/IntranetFirewall/policy/ifw/IntranetFwPolicyConfirm.vue";

export default defineComponent({
  name: "IntranetFwPolicyZoneList",
  props: {
    /** イントラネット契約情報(設定反映の表示用) */
    contractInfo: {
      type: Object as PropType<
        Pick<IntranetfwServiceMenu, "vpnCode" | "intranetFwBandwidth">
      >,
      required: true,
    },
  },
  data() {
    return {
      /** 通信完了フラグ */
      isLoaded: false,
      /** ゾーン接続情報 */
      zoneRelation: {} as ZoneRelation,
      zoneSelect: {
        sortBy: {
          key: "srcZoneName",
          order: "asc",
        } as SortItem,
        fields: [
          {
            key: "srcZoneName",
            title: "送信元ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v: string, _k: string, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.srcZoneType, "zoneType") +
                `/${item.srcZoneName}`
              );
            },
          },
          { key: "arrow", title: "" },
          {
            key: "dstZoneName",
            title: "宛先ゾーン/ゾーン名",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v: string, _k: string, item: ZoneRelationItem) => {
              return (
                this.$filter.enumTo(item.dstZoneType, "zoneType") +
                `/${item.dstZoneName}`
              );
            },
          },
        ],
        items: [] as ZoneRelationItem[],
      },
      /** ポリシー情報(イントラネットFW) */
      intranetFwPolicy: {} as IntranetFwPolicy,
      /** モーダル入室時のスナップショット */
      originalPolicy: {} as IntranetFwPolicy,
      uploadAlert: null as string | null,
    };
  },
  async mounted() {
    await this.load();
    this.originalPolicy = cloneDeep(this.intranetFwPolicy);
    this.isLoaded = true;
  },
  computed: {
    /** 変更があるかどうかを判定（policyIntranetFwKeyIdは比較から除外） */
    hasChanges(): boolean {
      const normalize = (p: IntranetFwPolicy) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyIntranetFwKeyId, ...rest } =
          p ?? ({} as IntranetFwPolicy);

        // 子componentで追加された空エントリを除外
        const filterEmptyEntries = (list: typeof rest.privateToPrivateList) => {
          return list.filter((item) => {
            // 内容がある場合は保持
            if (item.total > 0 || item.policyList.length > 0) {
              return true;
            }

            // 空エントリの場合、originalPolicyに存在するかチェック
            // ※ originalに存在 = API由来 → 保持、originalに不存在 = 子component追加 → 除外
            const existsInOriginal = this.originalPolicy[
              list === rest.privateToPrivateList
                ? "privateToPrivateList"
                : list === rest.extraToPrivateList
                  ? "extraToPrivateList"
                  : "privateToExtraList"
            ]?.some((orig) => orig.zoneRelationId === item.zoneRelationId);

            return existsInOriginal;
          });
        };

        rest.privateToPrivateList = filterEmptyEntries(
          rest.privateToPrivateList
        );
        rest.extraToPrivateList = filterEmptyEntries(rest.extraToPrivateList);
        rest.privateToExtraList = filterEmptyEntries(rest.privateToExtraList);

        return rest;
      };
      return !isEqual(
        normalize(this.intranetFwPolicy),
        normalize(this.originalPolicy)
      );
    },
  },
  methods: {
    /** ×ボタン／キャンセル押下時の共通クローズ処理 */
    async closeModal(): Promise<void> {
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }
      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch {
        // ダイアログでキャンセル → 何もしない
        return;
      }
    },
    /** データ取得 */
    async load() {
      // ゾーン接続情報をAPIから取得する
      this.zoneRelation = await this.$api.zoneRelation.getZoneRelation();
      // イントラネットFWのポリシー情報をAPIから取得する
      this.intranetFwPolicy =
        await this.$api.intranetFwPolicy.getPolicyIntranetFw("INTRANET_FW");
      // INTERNETのデータは非表示
      this.zoneSelect.items = this.zoneRelation.zoneRelationList.filter(
        (item) => {
          return (
            item.srcZoneType !== "INTERNET" && item.dstZoneType !== "INTERNET"
          );
        }
      );
    },
    /**
     * ポリシー一覧(イントラネットFW)の表示
     * @param row 選択した行のデータ
     */
    async ShowIntranetFwPolicyList(row: ZoneRelationItem) {
      this.uploadAlert = null;
      this.intranetFwPolicy = await this.$modal.show(IntranetFwPolicyList, {
        zoneRelationItem: row,
        policy: this.intranetFwPolicy,
        contractInfo: this.contractInfo,
      });
    },
    /** 設定ダウンロード */
    downloadConfig() {
      this.$service.configDownload.downloadIntranetFwPolicy(
        this.contractInfo.vpnCode,
        this.intranetFwPolicy,
        this.zoneRelation.zoneRelationList
      );
    },
    /** 設定アップロード */
    async uploadConfig() {
      try {
        const uploadPolicy =
          await this.$service.configDownload.uploadIntranetFwPolicy(
            this.contractInfo.vpnCode,
            this.intranetFwPolicy,
            this.zoneRelation.zoneRelationList
          );
        this.uploadAlert = null;
        await this.$confirm(
          "ポリシー設定を上書きします。設定反映していない設定は破棄されます。よろしいですか？"
        );
        this.intranetFwPolicy = uploadPolicy;
      } catch (e) {
        this.uploadAlert = (e as UploadError).message;
      }
    },
    /** ポリシー設定反映 */
    async putIntranetFwPolicy() {
      this.uploadAlert = null;
      // 設定反映確認モーダルを表示
      await this.$modal.show(IntranetFwPolicyConfirm, {
        intranetFwPolicy: this.intranetFwPolicy,
        zoneList: this.zoneSelect.items,
        contractInfo: this.contractInfo,
      });
      // APIに設定するデータを作成
      const reqData = createReqData(this.intranetFwPolicy);
      // ポリシー更新APIの呼び出し
      await this.$api.intranetFwPolicy.postPolicyIntranetFw(
        "INTRANET_FW",
        reqData
      );
      // モーダルを閉じる
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});

/** ポリシー更新APIの設定データ作成 */
function createReqData(policy: IntranetFwPolicy): PostIntranetPolicy {
  return {
    privateToPrivateList: policy.privateToPrivateList.map((v) => {
      return {
        zoneRelationId: v.zoneRelationId,
        policyList: v.policyList
          .filter((e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY")
          .map((e) => {
            // ポリシーカテゴリは更新時に不要なので含めない
            return {
              ...e,
              description: e.description || null,
              // アドレス・サービスはID、シーケンスのみ抽出する
              srcAddressList: e.srcAddressList.map((v) => v.ipAddressId),
              dstAddressList: e.dstAddressList.map((v) => v.ipAddressId),
              serviceList: e.serviceList.map((v) => v.serviceSeq),
              customServiceList: e.customServiceList.map(
                (v) => v.customServiceSeq
              ),
            };
          }),
      };
    }),
    extraToPrivateList: policy.extraToPrivateList.map((v) => {
      return {
        zoneRelationId: v.zoneRelationId,
        policyList: v.policyList
          .filter((e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY")
          .map((e) => {
            return {
              ...e,
              description: e.description || null,
              srcAddressList: e.srcAddressList.map((v) => v.ipAddressId),
              dstAddressList: e.dstAddressList.map((v) => v.ipAddressId),
              serviceList: e.serviceList.map((v) => v.serviceSeq),
              customServiceList: e.customServiceList.map(
                (v) => v.customServiceSeq
              ),
            };
          }),
      };
    }),
    privateToExtraList: policy.privateToExtraList.map((v) => {
      return {
        zoneRelationId: v.zoneRelationId,
        policyList: v.policyList
          .filter((e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY")
          .map((e) => {
            return {
              ...e,
              description: e.description || null,
              srcAddressList: e.srcAddressList.map((v) => v.ipAddressId),
              dstAddressList: e.dstAddressList.map((v) => v.ipAddressId),
              serviceList: e.serviceList.map((v) => v.serviceSeq),
              customServiceList: e.customServiceList.map(
                (v) => v.customServiceSeq
              ),
            };
          }),
      };
    }),
    // イントラネットFW指定なのでnullを設定
    wvs2ToKcps: null,
    kcpsToWvs2: null,
    note: null,
    policyIntranetFwKeyId: policy.policyIntranetFwKeyId,
  };
}
</script>
